import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Copy, Sparkles } from "lucide-react";
import { useSeo, breadcrumbSchema, CANONICAL_ORIGIN } from "../lib/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const humanCategory = (slug) => (slug || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function CommandPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [cmd, setCmd] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setCmd(null);
    setError(false);
    axios.get(`${API}/public/command/${slug}`)
      .then((r) => setCmd(r.data))
      .catch(() => setError(true));
    // Record anonymous view for admin "Most viewed" analytics (fire-and-forget).
    axios.post(`${API}/public/command/${slug}/view`).catch(() => {});
  }, [slug]);

  const title = cmd ? `${cmd.name} — AI Shorthand Command` : "AI Shorthand Command";
  const buildDescription = (c) => {
    if (!c) return "A short-command shortcut in the Universal Short-Command Prompt Dictionary™.";
    const catName = c.category_name || humanCategory(c.category);
    const firstExample = (c.examples || [])[0];
    const kw = (c.keywords || []).slice(0, 3).join(", ");
    // Aim for ~155 chars so Google shows the whole snippet without truncation.
    let d = `${c.name} — ready-to-use ${catName} short-command for ChatGPT, Claude & Gemini.`;
    if (firstExample) d += ` Use it to: ${firstExample.replace(/^\/[\w-]+\s*/, "").trim()}.`;
    if (kw) d += ` Keywords: ${kw}.`;
    return d.length > 300 ? d.slice(0, 297) + "…" : d;
  };
  const description = buildDescription(cmd);

  const jsonLd = cmd
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "HowTo",
            name: `How to use ${cmd.name} with ChatGPT, Claude or Gemini`,
            description,
            totalTime: "PT1M",
            supply: [{ "@type": "HowToSupply", name: "AI Shorthand membership" }],
            tool: (cmd.platforms || []).map((p) => ({ "@type": "HowToTool", name: p })),
            step: [
              { "@type": "HowToStep", name: "Open your AI tool", text: "Open ChatGPT, Claude or Gemini." },
              { "@type": "HowToStep", name: "Paste the command", text: `Paste the ${cmd.name} short-command with your details.` },
              { "@type": "HowToStep", name: "Get the result", text: "The AI delivers the finished output — copy, edit or use it." },
            ],
          },
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "By Category", path: "/categories" },
            { name: cmd.category_name || humanCategory(cmd.category), path: `/categories?cat=${encodeURIComponent(cmd.category)}` },
            { name: cmd.name, path: `/command/${cmd.slug}` },
          ]),
        ],
      }
    : null;

  useSeo({ title: cmd ? cmd.name : "Command", description, jsonLd, noindex: !cmd });

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Command not found</h1>
        <p className="mt-4 text-slate-500">This command isn't in the dictionary. It may have been renamed or removed.</p>
        <Link to="/categories" className="inline-block mt-8 text-emerald-700 font-semibold">Browse all categories →</Link>
      </div>
    );
  }

  if (!cmd) {
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-400" data-testid="command-loading">Loading…</div>;
  }

  const canonicalUrl = `${CANONICAL_ORIGIN}/command/${cmd.slug}`;

  return (
    <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16" data-testid={`command-page-${cmd.slug}`}>
      <nav className="text-xs text-slate-500 mb-4">
        <Link to="/" className="hover:text-emerald-700">Home</Link>
        <span className="mx-1.5">/</span>
        <Link to="/categories" className="hover:text-emerald-700">Categories</Link>
        <span className="mx-1.5">/</span>
        <Link to={`/categories?cat=${encodeURIComponent(cmd.category)}`} className="hover:text-emerald-700">
          {cmd.category_name || humanCategory(cmd.category)}
        </Link>
      </nav>

      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-wider">
        <Sparkles className="w-3.5 h-3.5" /> Short-Command Shortcut
      </div>

      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4 text-[#0B0F0D]" data-testid="command-name">
        <span className="text-emerald-700">/</span>{cmd.name.replace(/^\//, "")}
      </h1>

      <p className="mt-4 text-lg text-slate-600 leading-relaxed">
        A short-command shortcut in the{" "}
        <Link to={`/categories?cat=${encodeURIComponent(cmd.category)}`} className="text-emerald-700 font-semibold">
          {cmd.category_name || humanCategory(cmd.category)}
        </Link>{" "}
        category of the Universal Short-Command Prompt Dictionary™. Type <code className="px-1.5 py-0.5 bg-slate-100 rounded text-sm font-mono">{cmd.name}</code> in ChatGPT, Claude or Gemini instead of writing a long prompt from scratch.
      </p>

      {cmd.examples?.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-xl font-bold text-[#0B0F0D]">What you can ask</h2>
          <ul className="mt-4 space-y-3">
            {cmd.examples.map((ex, i) => (
              <li key={i} data-testid={`command-example-${i}`}
                className="p-4 bg-white border border-slate-200 rounded-xl text-slate-700 leading-relaxed">
                <span className="text-emerald-700 font-mono text-sm">{cmd.name}</span> {ex}
              </li>
            ))}
          </ul>
        </section>
      )}

      {cmd.keywords?.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-xl font-bold text-[#0B0F0D]">Also known as</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {cmd.keywords.map((k) => (
              <span key={k} className="px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">{k}</span>
            ))}
          </div>
        </section>
      )}

      <section className="mt-12 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-[#0B0F0D] to-[#1a2420] border border-[#D4AF37]/40 text-white">
        <h2 className="font-display text-2xl font-bold">Get the ready-to-use command</h2>
        <p className="mt-3 text-slate-300 leading-relaxed">
          Members get the full command with ready-made examples and the exact wording to paste into ChatGPT, Claude or Gemini. Plus hundreds more shortcuts across 42 categories.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={() => navigate("/pricing")} data-testid="command-cta-pricing"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#c9a233] text-[#0B0F0D] font-bold px-6 py-3 rounded-full transition-colors">
            See pricing <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => navigate("/register")} data-testid="command-cta-register"
            className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] font-bold px-6 py-3 rounded-full hover:bg-[#D4AF37]/10 transition-colors">
            Create account
          </button>
        </div>
      </section>

      {cmd.related?.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-xl font-bold text-[#0B0F0D]">Related commands</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {cmd.related.map((r) => (
              <Link key={r.slug} to={`/command/${r.slug}`} data-testid={`command-related-${r.slug}`}
                className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-emerald-500/50 hover:shadow-sm transition-all">
                <div>
                  <div className="font-semibold text-emerald-800 font-mono text-sm">{r.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{humanCategory(r.category)}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <p className="mt-16 text-xs text-slate-400">
        Canonical URL: <a href={canonicalUrl} className="hover:underline">{canonicalUrl}</a>
      </p>
    </div>
  );
}
